import "../../components/App.css";
import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  FormControl,
} from "@mui/material";
import { Alert, Spinner } from "react-bootstrap";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import countryToCurrency from "country-to-currency";
import axios from "axios";
import { API_BASE_URL } from "../../config/config";
import CaptchaField from "../../components/CaptchaField";
import { Link } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';
import CarePlanPaymentHandler from "../../components/Payment/CarePlanPaymentHandler";

const TravelCarePlan = ({ userDetails }) => {
  const [currencies, setCurrencies] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedSelfie, setSelectedSelfie] = useState(null);
  const [recaptchaToken, setRecaptchatToken] = useState("");
  const [user, setUser] = useState(null);

  useEffect(() => {
    const currencies = Object.values(countryToCurrency);
    const currency = [...new Set(currencies)];
    setCurrencies(currency);
  }, []);

  useEffect(() => {
    const currencies = Object.values(countryToCurrency);
    const currency = [...new Set(currencies)];
    setCurrencies(currency);
  }, []);

  const handleSelfieChange = (event) => {
    const file = event.target.files[0];
    setSelectedSelfie(file);
  };

  const calculateCareValues = (values, index) => {
    let careFees = 0;
    const careAmount = parseFloat(values.services[index].careValueAmount);
  
    let careExpireTimeline = values.services[index].travelTimeline;
  
    switch (values.services[index].category) {
      case "Domestic Trip":
        careFees = careAmount * 0.004;
        break;
      case "Hotel Stays":
        careFees = careAmount * 0.003;
        break;
      case "Local Cab Ride":
        careFees = careAmount * 0.005;
        break;
      default:
        break;
    }
  
    const numberOfFamilyMembers = values.services[index].familyMembers.length;
    careFees = careFees * numberOfFamilyMembers;
  
    values.services[index].careFees = careFees.toFixed(2);
    values.services[index].careExpireTimeline = careExpireTimeline;
  };
  
  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();

      formData.append("email", userDetails.email)
      formData.append("password", userDetails.password)
      formData.append("fName", userDetails.fName)
      formData.append("mName", userDetails.mName)
      formData.append("lName", userDetails.lName)
      formData.append("country", userDetails.country)
      formData.append("state", userDetails.state)
      formData.append("city", userDetails.city)
      formData.append("zip", userDetails.zip)
      formData.append("contact", userDetails.contact)

      values.services.forEach((service, index) => {
        calculateCareValues(values, index);

        formData.append("selfie", selectedSelfie);

        service.familyMembers.forEach((member, memberIndex) => {
          Object.entries(member).forEach(([memberKey, memberValue]) => {
            formData.append(
              `services[${index}].familyMembers[${memberIndex}][${memberKey}]`,
              memberValue
            );
          });
        });

        Object.entries(service).forEach(([key, value]) => {
          if (key !== "invoice" && key !== "familyMembers") {
            formData.append(`services[${index}][${key}]`, value);
          }
        })
      });

      Object.keys(values).forEach((key) => {
        if (key !== "services") {
          formData.append(key, values[key]);
        }
      });

      formData.append("recaptchaToken", recaptchaToken);

      await axios.post(`${API_BASE_URL}/register`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const token = localStorage.getItem('care_token');
      const tokenData = jwtDecode(token);
      const userId = tokenData.userId;
      const response = await axios.get(`${API_BASE_URL}/user?userId=${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    setUser(response.data.user);
    } catch (error) {
      console.error("Error:", error.response?.data?.error || error.message);
      setError("Error submitting the form");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCaptchaChange = (token) => {
    if (token) {
      setRecaptchatToken(token);
    }
  };

  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={["DatePicker"]}>
          <Formik
            initialValues={{
              services: [
                {
                  type: "Travel",
                  category: "",
                  itinerary: "",
                  careFor: "",
                  travelTimeline: "",
                  selfie: "",
                  familyMembers: [],
                  referralCode: "",
                  currency: "",
                  careFees: 0,
                  careValueAmount: 0,
                  careExpireTimeline: "",
                },
              ],
            }}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form>
                {formik.values.services.map((service, index) => (
                  <div key={index}>
                    <Grid container spacing={2} style={{ marginTop: "0" }}>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].category`}>
                          Select Product Category
                        </InputLabel>
                        <Field
                          as={Select}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].category`}
                          id={`services[${index}].category`}
                        >
                          <MenuItem value="Domestic Trip">
                            Domestic Trip
                          </MenuItem>
                          <MenuItem value="Hotel Stays">Hotel Stays</MenuItem>
                          <MenuItem value="Local Cab Ride">
                            Local Cab Ride
                          </MenuItem>
                        </Field>
                        <ErrorMessage name={`services[${index}].category`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].itinerary`}>
                          Itinerary (Travel Plans)
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].itinerary`}
                          id={`services[${index}].itinerary`}
                        />
                        <ErrorMessage name={`services[${index}].itinerary`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].careFor`}>
                          Care Value For
                        </InputLabel>
                        <div>
                          <FormControl component="fieldset">
                            <Field name={`services[${index}].careFor`}>
                              {({ field }) => (
                                <>
                                  <RadioGroup {...field} aria-label="careFor">
                                    <FormControlLabel
                                      value="Self"
                                      control={<Radio />}
                                      label="Self"
                                    />
                                    <FormControlLabel
                                      value="Family"
                                      control={<Radio />}
                                      label="Family"
                                    />
                                    <FormControlLabel
                                      value="Everyone"
                                      control={<Radio />}
                                      label="Everyone"
                                    />
                                  </RadioGroup>
                                </>
                              )}
                            </Field>
                          </FormControl>
                        </div>
                        <ErrorMessage name={`services[${index}].careFor`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].selfie`}>
                          Selfie
                        </InputLabel>
                        <input
                          style={{ width: "100%" }}
                          size="small"
                          type="file"
                          name={`services[${index}].selfie`}
                          id={`services[${index}].selfie`}
                          onChange={handleSelfieChange}
                          required
                        />
                        <ErrorMessage name={`services[${index}].selfie`} />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <InputLabel>Travel Timeline</InputLabel>
                        <FormControl component="fieldset">
                          <Field name={`services[${index}].travelTimeline`}>
                            {({ field }) => (
                              <>
                                <FormControlLabel
                                  control={<Radio />}
                                  label="1 Hour"
                                  value="1 Hour"
                                  {...field}
                                  checked={field.value === "1 Hour"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "1 Hour"
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="3 Hours"
                                  value="3 Hours"
                                  {...field}
                                  checked={field.value === "3 Hours"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "3 Hours"
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="6 Hours"
                                  value="6 Hours"
                                  {...field}
                                  checked={field.value === "6 Hours"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "6 Hours"
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="12 Hours"
                                  value="12 Hours"
                                  {...field}
                                  checked={field.value === "12 Hours"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "12 Hours"
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="24 Hours"
                                  value="24 Hours"
                                  {...field}
                                  checked={field.value === "24 Hours"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "24 Hours"
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="2 Days"
                                  value="2 Days"
                                  {...field}
                                  checked={field.value === "2 Days"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "2 Days"
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="Till 6 Months"
                                  value="Till 6 Months"
                                  {...field}
                                  checked={field.value === "Till 6 Months"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "Till 6 Months"
                                    )
                                  }
                                />
                              </>
                            )}
                          </Field>
                          <ErrorMessage
                            name={`services[${index}].travelTimeline`}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Typography variant="h6">Family Members</Typography>
                      </Grid>
                      {service.familyMembers.map((member, memberIndex) => (
                        <React.Fragment key={memberIndex}>
                          <Grid item xs={12} sm={3}>
                            <InputLabel
                              htmlFor={`services[${index}].familyMembers[${memberIndex}].name`}
                            >
                              Name
                            </InputLabel>
                            <Field
                              as={TextField}
                              style={{ width: "100%" }}
                              size="small"
                              name={`services[${index}].familyMembers[${memberIndex}].name`}
                              id={`services[${index}].familyMembers[${memberIndex}].name`}
                            />
                            <ErrorMessage
                              name={`services[${index}].familyMembers[${memberIndex}].name`}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <InputLabel
                              htmlFor={`services[${index}].familyMembers[${memberIndex}].age`}
                            >
                              Age
                            </InputLabel>
                            <Field
                              as={TextField}
                              style={{ width: "100%" }}
                              size="small"
                              name={`services[${index}].familyMembers[${memberIndex}].age`}
                              id={`services[${index}].familyMembers[${memberIndex}].age`}
                            />
                            <ErrorMessage
                              name={`services[${index}].familyMembers[${memberIndex}].age`}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <InputLabel
                              htmlFor={`services[${index}].familyMembers[${memberIndex}].sex`}
                            >
                              Sex
                            </InputLabel>
                            <Field
                              as={Select}
                              style={{ width: "100%" }}
                              size="small"
                              name={`services[${index}].familyMembers[${memberIndex}].sex`}
                              id={`services[${index}].familyMembers[${memberIndex}].sex`}
                            >
                              <MenuItem value="Male">Male</MenuItem>
                              <MenuItem value="Female">Female</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Field>
                            <ErrorMessage
                              name={`services[${index}].familyMembers[${memberIndex}].sex`}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <InputLabel
                              htmlFor={`services[${index}].familyMembers[${memberIndex}].nomineeDetails`}
                            >
                              Nominee Details
                            </InputLabel>
                            <Field
                              as={TextField}
                              style={{ width: "100%" }}
                              size="small"
                              name={`services[${index}].familyMembers[${memberIndex}].nomineeDetails`}
                              id={`services[${index}].familyMembers[${memberIndex}].nomineeDetails`}
                            />
                            <ErrorMessage
                              name={`services[${index}].familyMembers[${memberIndex}].nomineeDetails`}
                            />
                          </Grid>
                        </React.Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          type="button"
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            formik.setFieldValue(`services[0].familyMembers`, [
                              ...formik.values.services[0].familyMembers,
                              {
                                name: "",
                                age: "",
                                sex: "",
                                nomineeDetails: "",
                              },
                            ])
                          }
                        >
                          Add Family Member
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel>Select Currency</InputLabel>
                        <Field
                          as={Select}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].currency`}
                          id={`services[${index}].currency`}
                        >
                          <MenuItem value="">Select Currency</MenuItem>
                          {currencies.map((currency) => (
                            <MenuItem key={currency} value={currency}>
                              {currency}
                            </MenuItem>
                          ))}
                        </Field>
                        <ErrorMessage name={`services[${index}].currency`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel
                          htmlFor={`services[${index}].referralCode`}
                        >
                          Referral Code (If Any)
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].referralCode`}
                          id={`services[${index}].referralCode`}
                        />
                        <ErrorMessage name={`services[${index}].referralCode`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel
                          htmlFor={`services[${index}].careValueAmount`}
                        >
                          Enter Care Amount
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].careValueAmount`}
                          id={`services[${index}].careValueAmount`}
                        />
                        <ErrorMessage name={`services[${index}].careValueAmount`} />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <InputLabel
                          htmlFor={`services[${index}].careFees`}
                          align="center"
                        >
                          Care Fees to Pay
                        </InputLabel>
                        <Typography
                          variant="h6"
                          align="center"
                          name={`services[${index}].careFees`}
                        >
                          {service.category === "Domestic Trip" &&
      `${(service.careValueAmount * 0.004).toFixed(2)}`}
    {service.category === "Hotel Stays" &&
      `${(service.careValueAmount * 0.003).toFixed(2)}`}
    {service.category === "Local Cab Ride" &&
      `${(service.careValueAmount * 0.005).toFixed(2)}`}
    {service.currency && " " + service.currency}
                        </Typography>
                      </Grid>
                    </Grid>
                  </div>
                ))}
                <CaptchaField
                  onChange={handleCaptchaChange}
                  wrapperStyle={{ display: "flex" }}
                  style={{
                    margin: "0 auto",
                    textAlign: "center",
                    marginTop: "2rem",
                  }}
                />
                <div className="submission-popup">
                  <div className="loading">
                    {loading && <Spinner animation="border" />}
                  </div>
                  {error && <Alert variant="danger">{error}</Alert>}
                  {success && (
                    <Alert variant="success">
                      Form submitted successfully!
                    </Alert>
                  )}
                </div>
                <Grid container spacing={2} justifyContent="center">
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={loading}
                    style={{ margin: "0.5rem" }}
                  >
                    Submit & Pay
                  </Button>
                </Grid>
              </Form>
            )}
          </Formik>
        </DemoContainer>
      </LocalizationProvider>
      <CarePlanPaymentHandler user={user} userDetails={userDetails} />
      <div style={{ marginTop: "1rem", textAlign: "center" }}>
        <Typography variant="body1">
          Already a user? <Link to="/login">Login</Link>
        </Typography>
      </div>
    </div>
  );
};

export default TravelCarePlan;
